/*
  Sanal Garaj - global vehicle state + fitment-based product filtering.

  Selected vehicle is persisted to localStorage and broadcast via the
  `vehicle:changed` CustomEvent so any part of the theme (header badge,
  collection grids, product pages) can react without a shared framework.

  Product fitment data comes from two metafields, rendered as data
  attributes on `.product-card` by snippets/product-card.liquid and
  snippets/product-card-horizontal.liquid:
    - data-vehicle-ids="modelId1,engineId2,..."  (product.metafields.theme.compatible_vehicles)
    - data-universal-part                        (product.metafields.theme.universal_part)
*/
(function () {
  const STORAGE_KEY = `${theme.settings.themeName}:selected-vehicle`;

  const VehicleGarage = {
    get() {
      if (!theme.config.hasLocalStorage) return null;

      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      }
      catch (e) {
        return null;
      }
    },

    set(vehicle) {
      if (theme.config.hasLocalStorage) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
      }

      document.dispatchEvent(new CustomEvent('vehicle:changed', { detail: vehicle }));
    },

    clear() {
      if (theme.config.hasLocalStorage) {
        window.localStorage.removeItem(STORAGE_KEY);
      }

      document.dispatchEvent(new CustomEvent('vehicle:changed', { detail: null }));
    },

    matchesProduct(card) {
      const vehicle = this.get();
      if (!vehicle) return true;
      if (card.hasAttribute('data-universal-part')) return true;

      const raw = card.getAttribute('data-vehicle-ids');
      if (!raw) return true;

      const compatibleIds = raw.split(',').map((id) => id.trim()).filter(Boolean);
      if (compatibleIds.length === 0) return true;

      return compatibleIds.includes(vehicle.engineId) || compatibleIds.includes(vehicle.modelId);
    },

    applyToGrid(container = document) {
      const vehicle = this.get();
      const cards = container.querySelectorAll('[data-product-fitment]');

      cards.forEach((card) => {
        const matches = this.matchesProduct(card);
        card.hidden = !matches;
        card.classList.toggle('vehicle-filtered-out', !matches);
      });

      document.dispatchEvent(new CustomEvent('vehicle:grid-filtered', { detail: { vehicle } }));
    }
  };

  theme.vehicleGarage = VehicleGarage;

  class VehicleBadge extends HTMLElement {
    connectedCallback() {
      this.onVehicleChanged = this.render.bind(this);
      this.render();
      document.addEventListener('vehicle:changed', this.onVehicleChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('vehicle:changed', this.onVehicleChanged);
    }

    render() {
      const vehicle = VehicleGarage.get();
      const label = this.querySelector('[data-vehicle-label]');
      const empty = this.querySelector('[data-vehicle-empty]');
      const clearButton = this.querySelector('[data-vehicle-clear]');
      if (!label || !empty) return;

      if (vehicle) {
        label.textContent = vehicle.label;
        label.hidden = false;
        empty.hidden = true;
        if (clearButton) clearButton.hidden = false;
        this.setAttribute('data-has-vehicle', '');
      }
      else {
        label.hidden = true;
        empty.hidden = false;
        if (clearButton) clearButton.hidden = true;
        this.removeAttribute('data-has-vehicle');
      }
    }
  }
  customElements.define('vehicle-badge', VehicleBadge);

  document.addEventListener('click', (event) => {
    const clearButton = event.target.closest('[data-vehicle-clear]');
    if (!clearButton) return;

    event.preventDefault();
    VehicleGarage.clear();
  });

  const applyOnLoad = () => VehicleGarage.applyToGrid(document);
  theme.DOMready(applyOnLoad);

  document.addEventListener('vehicle:changed', applyOnLoad);
  document.addEventListener('collection:reloaded', applyOnLoad);
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, applyOnLoad);

  // Belt-and-suspenders: infinite-scroll / "load more" pagination (InfiniteButton
  // in collection.js) appends cards without dispatching collection:reloaded or
  // facetUpdate, so watch the grid container directly for new cards.
  theme.DOMready(() => {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;

    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => VehicleGarage.applyToGrid(grid), 50);
    });

    observer.observe(grid, { childList: true, subtree: true });
  });
})();
